import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import loveone   from '../images/loveone.jpg';
import lovetwo   from '../images/lovetwo.jpg';
import lovethree from '../images/lovethree.jpg';
import lovefour  from '../images/lovefour.jpg';
import lovefive  from '../images/lovefive.jpg';

/* ─── Design Tokens ──────────────────────────────────────────── */
const gold    = '#c9a96e';
const cream   = '#f5f0e8';
const ink     = '#0e0c0a';
const display = `'Cormorant Garamond', Georgia, serif`;
const sans    = `'Jost', sans-serif`;
const slab    = `'Bebas Neue', sans-serif`;

/* ─── All Images ─────────────────────────────────────────────── */
const images = [
  { src: loveone,   caption: 'The Ceremony'    },
  { src: lovetwo,   caption: 'The Celebration' },
  { src: lovethree, caption: 'The Romance'     },
  { src: lovefour,  caption: 'The Details'     },
  { src: lovefive,  caption: 'The Moments'     }
];

/* ─── Keyframes ──────────────────────────────────────────────── */
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/* ─── Motion Variants ────────────────────────────────────────── */
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════ */
const Gallery = () => {
  const [activeIdx, setActiveIdx] = useState(null);
  const isOpen = activeIdx !== null;

  /* keyboard nav */
  const handleKey = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape')     setActiveIdx(null);
    if (e.key === 'ArrowRight') setActiveIdx(i => Math.min(i + 1, images.length - 1));
    if (e.key === 'ArrowLeft')  setActiveIdx(i => Math.max(i - 1, 0));
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  /* lock body scroll when lightbox open */
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <GalleryWrapper>
      {/* ── Section header ── */}
      <GalleryHeader
        as={motion.div}
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        <GalEyebrow as={motion.div} variants={fadeUp}>The Gallery</GalEyebrow>
        <GalTitle as={motion.div} variants={fadeUp}>
          Captured<br /><em>Moments</em>
        </GalTitle>
        <GalSub as={motion.div} variants={fadeUp}>
          Click any photograph to view it in full
        </GalSub>
      </GalleryHeader>

      {/* ── Masonry grid ── */}
      <MasonryWrap
        as={motion.div}
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-40px' }}
      >
        {images.map((img, i) => (
          <MasonryItem
            key={i}
            as={motion.div}
            variants={fadeUp}
            $tall={i === 0 || i === 4 || i === 7}
            whileHover="hover"
            onClick={() => setActiveIdx(i)}
          >
            <MasonryImg src={img.src} alt={img.caption} loading="lazy" />
            <MasonryOverlay
              as={motion.div}
              initial={{ opacity: 0 }}
              variants={{ hover: { opacity: 1, transition: { duration: 0.3 } } }}
            >
              <MasonryCaptionNum>0{i + 1}</MasonryCaptionNum>
              <MasonryCaptionText>{img.caption}</MasonryCaptionText>
              <MasonryExpandIcon>↗</MasonryExpandIcon>
            </MasonryOverlay>
          </MasonryItem>
        ))}
      </MasonryWrap>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {isOpen && (
          <Lightbox
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActiveIdx(null)}
          >
            {/* Close */}
            <LBClose
              onClick={(e) => { e.stopPropagation(); setActiveIdx(null); }}
              whileHover={{ rotate: 90, scale: 1.1 }}
              transition={{ duration: 0.25 }}
            >✕</LBClose>

            {/* Hint */}
            <LBHint>← → arrow keys to navigate · ESC to close</LBHint>

            {/* Counter */}
            <LBCounter>{activeIdx + 1} / {images.length}</LBCounter>

            {/* Image */}
            <LBImgWrap onClick={(e) => e.stopPropagation()}>
              <AnimatePresence mode="wait">
                <LBImg
                  key={activeIdx}
                  as={motion.img}
                  src={images[activeIdx].src}
                  alt={images[activeIdx].caption}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                />
              </AnimatePresence>
            </LBImgWrap>

            {/* Caption */}
            <LBCaption onClick={(e) => e.stopPropagation()}>
              <LBCaptionSub>Little Heaven Bed &amp; Breakfast</LBCaptionSub>
              <LBCaptionTitle>{images[activeIdx].caption}</LBCaptionTitle>
            </LBCaption>

            {/* Prev / Next */}
            <LBPrev
              onClick={(e) => { e.stopPropagation(); setActiveIdx(i => Math.max(i - 1, 0)); }}
              disabled={activeIdx === 0}
              whileHover={{ x: -3 }}
            >←</LBPrev>
            <LBNext
              onClick={(e) => { e.stopPropagation(); setActiveIdx(i => Math.min(i + 1, images.length - 1)); }}
              disabled={activeIdx === images.length - 1}
              whileHover={{ x: 3 }}
            >→</LBNext>

            {/* Thumbnail strip */}
            <LBThumbs onClick={(e) => e.stopPropagation()}>
              {images.map((img, i) => (
                <LBThumb
                  key={i}
                  $active={i === activeIdx}
                  onClick={() => setActiveIdx(i)}
                  whileHover={{ scale: 1.08 }}
                >
                  <img src={img.src} alt={img.caption} />
                </LBThumb>
              ))}
            </LBThumbs>
          </Lightbox>
        )}
      </AnimatePresence>
    </GalleryWrapper>
  );
};

export default Gallery;

/* ═══════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════ */

const GalleryWrapper = styled.div`
  width: 100%;
  background: #f9f5ee;
  padding: 0 0 100px;
`;

/* ── Header ───────────────────────────────────────────────────── */
const GalleryHeader = styled.div`
  text-align: center;
  padding: 80px 24px 60px;
`;

const GalEyebrow = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${gold};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;

  &::before, &::after {
    content: '';
    display: inline-block;
    width: 26px;
    height: 1px;
    background: ${gold};
    opacity: 0.5;
  }
`;

const GalTitle = styled.h2`
  font-family: ${display};
  font-size: clamp(3rem, 6vw, 5.5rem);
  font-weight: 300;
  line-height: 1.0;
  color: ${ink};
  margin: 0 0 18px;
  letter-spacing: -0.5px;

  em { font-style: italic; color: ${gold}; }
`;

const GalSub = styled.p`
  font-family: ${sans};
  font-size: 0.88rem;
  font-weight: 300;
  letter-spacing: 1.5px;
  color: rgba(40,32,24,0.5);
  margin: 0;
`;

/* ── Masonry ──────────────────────────────────────────────────── */
const MasonryWrap = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
  columns: 3;
  column-gap: 16px;

  @media (max-width: 1024px) { columns: 2; }
  @media (max-width: 580px)  { columns: 1; padding: 0 16px; }
`;

const MasonryItem = styled.div`
  position: relative;
  break-inside: avoid;
  margin-bottom: 16px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid rgba(201,169,110,0.12);

  &:hover { z-index: 2; }
`;

const MasonryImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
  transition: transform 0.75s cubic-bezier(0.22, 1, 0.36, 1);

  ${MasonryItem}:hover & { transform: scale(1.05); }
`;

const MasonryOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(14,12,10,0.0) 30%, rgba(14,12,10,0.88) 100%);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 24px;
`;

const MasonryCaptionNum = styled.div`
  font-family: ${slab};
  font-size: 2.5rem;
  color: rgba(201,169,110,0.25);
  line-height: 1;
  margin-bottom: 4px;
`;

const MasonryCaptionText = styled.div`
  font-family: ${display};
  font-size: 1.3rem;
  font-style: italic;
  font-weight: 300;
  color: ${cream};
  line-height: 1;
`;

const MasonryExpandIcon = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(201,169,110,0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${ink};
  font-weight: 600;
`;

/* ── Lightbox ─────────────────────────────────────────────────── */
const Lightbox = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(8, 6, 4, 0.97);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 72px 20px 130px;
  backdrop-filter: blur(14px);
  cursor: pointer;
`;

const LBClose = styled(motion.button)`
  position: fixed;
  top: 24px;
  right: 32px;
  background: rgba(201,169,110,0.12);
  border: 1px solid ${gold};
  color: ${gold};
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  font-family: ${sans};

  &:hover { background: ${gold}; color: ${ink}; }

  @media (max-width: 600px) { top: 14px; right: 14px; }
`;

const LBHint = styled.div`
  position: fixed;
  top: 34px;
  left: 32px;
  font-family: ${sans};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(245,240,232,0.28);
  z-index: 10002;

  @media (max-width: 768px) { display: none; }
`;

const LBCounter = styled.div`
  position: fixed;
  top: 36px;
  left: 50%;
  transform: translateX(-50%);
  font-family: ${slab};
  font-size: 1rem;
  letter-spacing: 3px;
  color: rgba(201,169,110,0.55);
  z-index: 10002;
`;

const LBImgWrap = styled.div`
  width: 100%;
  max-width: 1100px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: default;
`;

const LBImg = styled.img`
  max-width: 100%;
  max-height: 66vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 40px 100px rgba(0,0,0,0.9);
  display: block;
`;

const LBCaption = styled.div`
  text-align: center;
  margin-top: 22px;
  cursor: default;
`;

const LBCaptionSub = styled.div`
  font-family: ${sans};
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(201,169,110,0.55);
  margin-bottom: 6px;
`;

const LBCaptionTitle = styled.div`
  font-family: ${display};
  font-size: 1.6rem;
  font-style: italic;
  font-weight: 300;
  color: ${cream};
`;

const LBNavBtn = styled(motion.button)`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(201,169,110,0.1);
  border: 1px solid rgba(201,169,110,0.3);
  color: ${gold};
  width: 52px;
  height: 52px;
  border-radius: 50%;
  font-size: 1.3rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
  font-family: ${sans};
  transition: background 0.25s, border-color 0.25s;

  &:hover:not(:disabled) {
    background: rgba(201,169,110,0.25);
    border-color: ${gold};
  }

  &:disabled {
    opacity: 0.2;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    top: auto;
    bottom: 160px;
    transform: none;
  }
`;

const LBPrev = styled(LBNavBtn)`
  left: 24px;
  @media (max-width: 600px) { left: 24px; }
`;

const LBNext = styled(LBNavBtn)`
  right: 24px;
  @media (max-width: 600px) { right: 24px; }
`;

const LBThumbs = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px 18px;
  background: linear-gradient(180deg, transparent, rgba(8,6,4,0.95));
  cursor: default;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
`;

const LBThumb = styled(motion.div)`
  width: 58px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid ${(p) => p.$active ? gold : 'transparent'};
  opacity: ${(p) => p.$active ? 1 : 0.45};
  transition: opacity 0.25s, border-color 0.25s;

  &:hover { opacity: 1; }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;